'use client';

import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useCommandMessages } from '@/hooks/use-command-messages';
import { useCommandSessions, useCachedCommandSession, resolveCommandSessionAgentId } from '@/hooks/use-command-sessions';
import { useCommandSession } from '@/components/aios-command/chat/command-session-context';
import { post, fetchData, patch, postStream } from '@/utils/api-handler';
import { useFileAttachmentQueue, ACCEPT_FILES } from '@/hooks/use-file-attachment-queue';
import { useProtectedMutation } from '@/hooks/use-protected-mutation';
import { toast } from 'sonner';
import { CommandSession, CommandAgent } from '@/types/command-agent';
import type { FormEventHandler } from 'react';
import { Conversation, ConversationContent, ConversationScrollButton } from '@/components/ui/shadcn-io/ai/conversation';
import { Message, MessageContent } from '@/components/ai';
import {
    PromptInput,
    PromptInputTextarea,
} from '@/components/ui/shadcn-io/ai/prompt-input';
import { Source, Sources, SourcesContent, SourcesTrigger } from '@/components/ui/shadcn-io/ai/source';
import { Response } from '@/components/ui/shadcn-io/ai/response';
import {
    Branch,
    BranchMessages,
    BranchNext,
    BranchPage,
    BranchPrevious,
    BranchSelector,
} from '@/components/ui/shadcn-io/ai/branch';
import { CopyIcon, XIcon, SquareIcon, Loader2Icon, RefreshCw, Upload, ChevronLeft, ChevronRight, Plus, Star, Send, MessageSquareX, CalendarClock } from 'lucide-react';
import { FluidOrb } from '@/components/ui/fluid-orb';
import { AgentSettingsSheet } from './agent-settings-sheet';
import { ComposerPresetsButton, COMPOSER_TOOLBAR_PILL_CLASS } from './composer-presets-button';
import { CollapsibleUserMessage } from './collapsible-user-message';
import { ContinueFlowSuggestions } from './continue-flow-suggestions';
import { ExtendedChatMessage, ChatAreaProps } from './chat-types';
import { DEFAULT_MODELS, MAX_GENERATIONS_PER_MESSAGE, MODEL_INIT_DELAY } from './chat-constants';
import { AssistantMessageContent, getAssistantErrorText } from './message-renderer';
import {
    createEventHandler,
    processSSEChunk,
    type SSEEventHandler,
} from './sse-event-handler';
import { useStreamReconnect } from './use-stream-reconnect';
import { useMergedMessages } from './use-merged-messages';
import { useConversationScroll } from './use-conversation-scroll';
import { useSendMessage } from './use-send-message';
import { AccessDeniedCard } from '@/components/shared/access-denied-card';
import { motion, AnimatePresence } from 'framer-motion';
import { PresetPromptCards } from './preset-prompt-cards';
import type { PromptParentTabMode } from './prompt-card-metadata';
import { cn } from '@/lib/utils';
import { MessageAttachments } from '@/components/aios-command/chat/message-attachments';
import { Skeleton } from '@/components/ui/skeleton';
import {
    usePresets,
    useFavoritePresets,
    useToggleFavorite,
    usePresetParentCategories,
    presetParentCategoryPillLabel,
} from '@/hooks/use-presets';
import { HorizontalChevronChipRow } from '@/components/prompt-library/parent-category-pill-scroller';
import { CommandSchedulerDialog } from './command-scheduler-dialog';
import { useCreateCommandScheduleJob } from '@/hooks/use-command-scheduler-job';
import type { CommandSchedulerSubmitInput } from '@/types/command-scheduler';
import { useAppSelector } from '@/redux/hooks';
import { useIsMobile } from '@/hooks/use-mobile';
import { TrialPlanBanner } from '@/components/aios-command/chat/trial-plan-banner';

function createRandomSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatMessageTime(timestamp: string | number | Date): string {
    const date = new Date(timestamp);
    const now = new Date();
    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return `Today, ${time}`;
    if (isYesterday) return `Yesterday, ${time}`;

    const isThisYear = date.getFullYear() === now.getFullYear();
    if (isThisYear) {
        return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
    }
    return `${date.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' })}, ${time}`;
}

interface LLMModel {
    provider: string;
    model_key: string;
    model_id: string;
    display_name: string;
    default_max_tokens?: number;
    default_temperature?: number;
}

interface LLMModelsResponse {
    models: LLMModel[];
    total: number;
}

const commandEmptyStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
} as const;

const commandEmptyItem = {
    hidden: { opacity: 0, y: 6 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
    },
} as const;

const commandEmptyEase = [0.22, 1, 0.36, 1] as const;

/** Drafts-map key for the "new chat" composer (no session id yet). */
const NEW_CHAT_DRAFT_KEY = '__new_chat__';


export function ChatArea({
    sessionId: initialSessionId,
    agentId,
    onNewSessionCreated,
    onSessionTitleUpdate,
}: ChatAreaProps) {
    const {
        promptLibraryActiveCategory: activeCategory,
        setPromptLibraryActiveCategory: setActiveCategory,
        activeSessionId,
        activeSessionAgentId,
    } = useCommandSession();
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();
    const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const inputValueRef = useRef(inputValue);
    inputValueRef.current = inputValue;
    /** Unsent draft text per session, restored when switching back. Null session (new chat) uses NEW_CHAT_DRAFT_KEY. */
    const sessionDraftsRef = useRef<Record<string, string>>({});
    const [selectedModel, setSelectedModel] = useState<string>('');
    const [selectedProvider, setSelectedProvider] = useState<string>('');
    const [isTyping, setIsTyping] = useState(false);
    const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
    const [, setStreamingPhase] = useState<'working' | 'responding'>('working');
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(initialSessionId || null);
    /** URL / parent session id wins so the messages query never lags one render behind local state when switching chats. */
    const sessionIdForMessages = initialSessionId ?? currentSessionId ?? null;
    const cachedActiveSession = useCachedCommandSession(sessionIdForMessages);
    /** Sessions live under a specific agent; never blind-fallback to URL agent when cache knows the owner. */
    const sessionAgentId = resolveCommandSessionAgentId(sessionIdForMessages, agentId, {
        cachedSession: cachedActiveSession,
        storedSessionAgentId:
            sessionIdForMessages && sessionIdForMessages === activeSessionId
                ? activeSessionAgentId
                : null,
    });
    const currentSessionIdRef = useRef(currentSessionId);
    currentSessionIdRef.current = currentSessionId;
    const isTypingRef = useRef(isTyping);
    isTypingRef.current = isTyping;
    const streamingMessageIdRef = useRef(streamingMessageId);
    streamingMessageIdRef.current = streamingMessageId;
    const fetchedMessagesRef = useRef<typeof fetchedMessages>([]);
    const [, setIsFirstMessage] = useState(!initialSessionId);
    const conversationRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const lastActiveCategoryRef = useRef<string>('');
    const pendingMessagesRef = useRef<ExtendedChatMessage[]>([]);
    const activeSessionRef = useRef<string | null>(initialSessionId || null);
    const isInitializingRef = useRef<boolean>(true);
    const prevSessionIdRef = useRef<string | null>(initialSessionId || null);
    /** In-flight stream controllers. Each stream adds its own; session-switch aborts the lot. */
    const streamAbortRef = useRef<Set<AbortController>>(new Set());
    const streamCancelledRef = useRef<boolean>(false);
    const stickyContextRef = useRef<any>(null);
   
    const syncingFromAgentRef = useRef<boolean>(false);
    const [favouritePromptIds, setFavouritePromptIds] = useState<Set<string>>(() => new Set());

    const { data: presetParentsResponse, isLoading: isLoadingPresetParents } = usePresetParentCategories();
    const parentCategoryItems = presetParentsResponse?.items ?? [];

    const PROMPT_CATEGORY_FAVOURITES = 'Favourites';
    const PROMPT_CATEGORY_CUSTOM = 'Custom';

    const activeParentCategoryItem = useMemo(
        () => parentCategoryItems.find((i) => i.id === activeCategory),
        [parentCategoryItems, activeCategory]
    );

    const promptParentTabMode: PromptParentTabMode = activeParentCategoryItem
        ? activeParentCategoryItem.name.toUpperCase() === 'MANAGEMENT' ||
          activeParentCategoryItem.name.toUpperCase() === 'DO'
            ? 'management'
            : 'executive'
        : (activeCategory ?? '').toUpperCase() === 'MANAGEMENT' || (activeCategory ?? '').toUpperCase() === 'DO'
          ? 'management'
          : 'executive';

    useEffect(() => {
        if (parentCategoryItems.length === 0) return;
        setActiveCategory((prev) => {
            if (prev === PROMPT_CATEGORY_FAVOURITES || prev === PROMPT_CATEGORY_CUSTOM || prev === '') return prev;
            if (prev === 'All') return '';

            if (parentCategoryItems.some((i) => i.id === prev)) return prev;

            const upper = (prev ?? '').toUpperCase();
            if (prev === 'See' || prev === 'Executive' || upper === 'EXECUTIVE') {
                const hit = parentCategoryItems.find((i) => i.name.toUpperCase() === 'EXECUTIVE');
                return hit?.id ?? parentCategoryItems[0]?.id ?? prev;
            }
            if (prev === 'Do' || prev === 'Manager' || upper === 'MANAGEMENT') {
                const hit = parentCategoryItems.find((i) => i.name.toUpperCase() === 'MANAGEMENT');
                return hit?.id ?? parentCategoryItems[0]?.id ?? prev;
            }
            const byName = parentCategoryItems.find((i) => i.name === prev);
            if (byName) return byName.id;

            return parentCategoryItems[0]?.id ?? prev;
        });
    }, [parentCategoryItems, setActiveCategory]);

    useEffect(() => {
        if (!isMobile) return;
        const viewport = window.visualViewport;
        if (!viewport) return;
        let prevHeight = viewport.height;
        const handleResize = () => {
            const curr = viewport.height;
            const keyboardJustClosed = curr > prevHeight + 100;
            if (keyboardJustClosed && lastActiveCategoryRef.current !== '') {
                setActiveCategory(lastActiveCategoryRef.current);
            }
            prevHeight = curr;
        };
        viewport.addEventListener('resize', handleResize);
        return () => viewport.removeEventListener('resize', handleResize);
    }, [isMobile, setActiveCategory]);

    const presetsListParentCategoryId = useMemo((): string | undefined => {
        if (
            activeCategory === PROMPT_CATEGORY_FAVOURITES ||
            activeCategory === PROMPT_CATEGORY_CUSTOM ||
            activeCategory === '' ||
            activeCategory === 'All'
        ) {
            return undefined;
        }
        if (parentCategoryItems.length === 0) {
            return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(activeCategory)
                ? activeCategory
                : undefined;
        }
        const hit = parentCategoryItems.find((i) => i.id === activeCategory);
        return hit?.id;
    }, [activeCategory, parentCategoryItems]);
    const [presetCardsScrollRoot, setPresetCardsScrollRoot] = useState<Element | null>(null);
    const handlePresetCardsScrollRoot = useCallback((el: HTMLDivElement | null) => {
        setPresetCardsScrollRoot(el);
    }, []);
    const { promptItems: apiPromptItems, isLoading: isLoadingPresets, isFetchingNextPage: isFetchingNextPresets, hasNextPage: hasNextPresets, loadMoreRef: presetsLoadMoreRef     } = usePresets({
        parentCategoryId: presetsListParentCategoryId,
        intersectionRoot: presetCardsScrollRoot,
    });
    const {
        promptItems: myPresetPromptItems,
        isLoading: isLoadingMyPresets,
        isFetchingNextPage: isFetchingNextMyPresets,
        hasNextPage: hasNextMyPresets,
        loadMoreRef: myPresetsLoadMoreRef,
    } = usePresets({
        parentCategoryId: presetsListParentCategoryId,
        myPresets: true,
        enabled: activeCategory === PROMPT_CATEGORY_CUSTOM,
        intersectionRoot: presetCardsScrollRoot,
    });
    const { favoriteIds: apiFavoriteIds, favoritePromptItems: apiFavoritePromptItems, isLoading: isLoadingFavorites } = useFavoritePresets({
        parentCategoryId: presetsListParentCategoryId,
    });
    const { toggleFavorite: apiToggleFavorite } = useToggleFavorite();

    const [settingsSheetOpen, setSettingsSheetOpen] = useState(false);
    const [centerScheduleOpen, setCenterScheduleOpen] = useState(false);
    const {
        attachedFiles,
        isDraggingOver,
        fileInputRef,
        handleFileSelect,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handlePaste,
        removeAttachedFile,
        clear: clearAttachments,
    } = useFileAttachmentQueue();


    const [canScrollAttachedLeft, setCanScrollAttachedLeft] = useState(false);
    const [canScrollAttachedRight, setCanScrollAttachedRight] = useState(false);
    const attachedScrollRef = useRef<HTMLDivElement>(null);
    const attachedRoRef = useRef<ResizeObserver | null>(null);
    const attachedCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const check = (el: HTMLDivElement | null, setLeft: (v: boolean) => void, setRight: (v: boolean) => void) => {
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const overflow = scrollWidth > clientWidth;
        const th = 10;
        setLeft(overflow && scrollLeft > th);
        setRight(overflow && scrollWidth - clientWidth - scrollLeft > th);
    };
    const scroll = (el: HTMLDivElement | null, dir: 'left' | 'right', setLeft: (v: boolean) => void, setRight: (v: boolean) => void) => {
        if (!el) return;
        el.scrollTo({ left: dir === 'left' ? el.scrollLeft - 400 : el.scrollLeft + 400, behavior: 'smooth' });
        requestAnimationFrame(() => check(el, setLeft, setRight));
    };
    const setAttachedRef = useCallback((node: HTMLDivElement | null) => {
        attachedCheckTimeoutRef.current && clearTimeout(attachedCheckTimeoutRef.current);
        attachedCheckTimeoutRef.current = null;
        attachedScrollRef.current = node;
        attachedRoRef.current?.disconnect();
        attachedRoRef.current = null;
        if (node) {
            const runCheck = () => check(node, setCanScrollAttachedLeft, setCanScrollAttachedRight);
            setTimeout(runCheck, 0);
            requestAnimationFrame(() => requestAnimationFrame(runCheck));
            attachedCheckTimeoutRef.current = setTimeout(runCheck, 150);
            const ro = new ResizeObserver(() => setTimeout(runCheck, 0));
            ro.observe(node);
            if (node.firstElementChild) ro.observe(node.firstElementChild);
            attachedRoRef.current = ro;
        }
    }, []);

    const { data: agent, isLoading: isLoadingAgent } = useQuery<CommandAgent>({
        queryKey: ["command-agent", agentId],
        queryFn: async () => {
            const response = await fetchData(`/api/command-team/agents/${agentId}`);
            return response;
        },
        enabled: Boolean(agentId),
    });

    const { data: llmModelsData, isLoading: isLoadingModels } = useQuery<LLMModelsResponse>({
        queryKey: ["llm-models"],
        queryFn: () => fetchData("/api/command-team/llm-config/models"),
    });

    
    const filteredModels = useMemo(() => {
        if (!llmModelsData?.models) return [];
        if (!selectedProvider) return [];
        return llmModelsData.models.filter((model) => model.provider === selectedProvider);
    }, [llmModelsData?.models, selectedProvider]);

    
    const availableProviders = useMemo(() => {
        if (!llmModelsData?.models) return [];
        const providers = new Set(llmModelsData.models.map((model) => model.provider));
        return Array.from(providers).sort();
    }, [llmModelsData?.models]);

    useEffect(() => {
        if (isLoadingAgent || isLoadingModels) return;

        syncingFromAgentRef.current = true;

        if (agent?.llm_provider) {
            setSelectedProvider(agent.llm_provider);
        } else if (!selectedProvider && availableProviders.length > 0) {
            setSelectedProvider(availableProviders[0]);
        }

        if (agent?.llm_model) {
            const agentModel = llmModelsData?.models?.find(
                (model) => model.model_key === agent.llm_model || model.model_id === agent.llm_model
            );
            if (agentModel) {
                setSelectedProvider(agentModel.provider);
                setSelectedModel(agentModel.model_key);
            } else if (agent.llm_model !== selectedModel) {
                setSelectedModel(agent.llm_model);
            }
        } else if (!selectedModel && filteredModels.length > 0) {
            setSelectedModel(filteredModels[0].model_key);
        } else if (!selectedModel && DEFAULT_MODELS.length > 0) {
            setSelectedModel(DEFAULT_MODELS[0].id);
        }

        const timer = setTimeout(() => {
            isInitializingRef.current = false;
            syncingFromAgentRef.current = false;
        }, MODEL_INIT_DELAY);

        return () => clearTimeout(timer);
    }, [agent, isLoadingAgent, isLoadingModels, selectedModel, selectedProvider, filteredModels, availableProviders, llmModelsData]);

    const updateModelMutation = useMutation({
        mutationFn: async ({ model, provider }: { model: string; provider?: string }) => {
            if (!agentId) throw new Error('Agent ID is required');
            if (!agent) throw new Error('Agent data is not loaded');
            const updateData: {
                llm_model: string;
                llm_provider?: string;
                llm_max_tokens?: number;
                llm_temperature?: number;
            } = { llm_model: model };

            const nextModelConfig = llmModelsData?.models?.find(
                (m) => m.model_key === model || m.model_id === model,
            );
            if (nextModelConfig) {
                updateData.llm_max_tokens = nextModelConfig.default_max_tokens;
                updateData.llm_temperature = nextModelConfig.default_temperature;
            }

            if (provider) {
                updateData.llm_provider = provider;
            }
            const response = await patch(`/api/command-team/agents/${agentId}`, updateData);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["command-agent", agentId] });
            queryClient.invalidateQueries({ queryKey: ["command-agent-header", agentId] });
            toast.success('Model updated successfully');
        },
        onError: (error: any) => {
            if (error.message !== 'Agent data is not loaded') {
                toast.error("Failed to update model", {
                    description: error.response?.data?.message || "An unexpected error occurred",
                });
            }
        },
    });

    const handleModelChange = useCallback((model: string) => {
        setSelectedModel(model);
        if (settingsSheetOpen || syncingFromAgentRef.current || isInitializingRef.current || isLoadingAgent || !agent || model === agent.llm_model) return;
        updateModelMutation.mutate({ model, provider: selectedProvider });
    }, [agent, isLoadingAgent, updateModelMutation, selectedProvider, settingsSheetOpen]);

    const categoriesScroll = useMemo(
        () => [PROMPT_CATEGORY_CUSTOM, ...parentCategoryItems.map((i) => i.id)],
        [parentCategoryItems]
    );

    /** Filled pill for Custom and API parent categories (not used for Favourites). */
    const promptCategoryChipBase =
        'group shrink-0 inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors sm:h-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
    const promptCategoryChipActive =
        'bg-foreground text-background shadow-sm dark:bg-zinc-700 dark:text-zinc-50 dark:shadow-[0_1px_2px_rgba(0,0,0,0.45)]';
    const promptCategoryChipInactive =
        'text-muted-foreground hover:text-foreground';

    const togglePromptFavourite = useCallback((id: string) => {
        apiToggleFavorite(id);
        // Optimistic local toggle for instant UI feedback
        setFavouritePromptIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, [apiToggleFavorite]);

    // Merge API favorite IDs with local optimistic state
    const mergedFavouriteIds = useMemo(() => {
        const merged = new Set(apiFavoriteIds);
        favouritePromptIds.forEach((id) => {
            if (merged.has(id)) merged.delete(id);
            else merged.add(id);
        });
        return merged;
    }, [apiFavoriteIds, favouritePromptIds]);

    // Reset local optimistic state when API data refreshes
    useEffect(() => {
        setFavouritePromptIds(new Set());
    }, [apiFavoriteIds]);

    const promptLibraryPrompts = useMemo(() => {
        if (activeCategory === '') return [];
        if (activeCategory === PROMPT_CATEGORY_FAVOURITES) return apiFavoritePromptItems;
        if (activeCategory === PROMPT_CATEGORY_CUSTOM) return myPresetPromptItems;
        return apiPromptItems;
    }, [
        activeCategory,
        apiPromptItems,
        apiFavoritePromptItems,
        myPresetPromptItems,
        PROMPT_CATEGORY_FAVOURITES,
        PROMPT_CATEGORY_CUSTOM,
    ]);

    const isLoadingActiveCategoryPresets =
        activeCategory === PROMPT_CATEGORY_CUSTOM ? isLoadingMyPresets
        : activeCategory === PROMPT_CATEGORY_FAVOURITES ? isLoadingFavorites
        : isLoadingPresets;
    const isFetchingNextActiveCategoryPresets =
        activeCategory === PROMPT_CATEGORY_CUSTOM ? isFetchingNextMyPresets : isFetchingNextPresets;
    const hasNextActiveCategoryPresets =
        activeCategory === PROMPT_CATEGORY_CUSTOM ? hasNextMyPresets : hasNextPresets;
    const activePresetsLoadMoreRef =
        activeCategory === PROMPT_CATEGORY_CUSTOM ? myPresetsLoadMoreRef : presetsLoadMoreRef;

    useLayoutEffect(() => {
        const previousSessionId = prevSessionIdRef.current;
        const newSessionId = initialSessionId || null;

        if (newSessionId) {
            setCurrentSessionId(newSessionId);
            setIsFirstMessage(false);
        } else {
            setCurrentSessionId(null);
            setIsFirstMessage(true);
        }

        if (previousSessionId !== newSessionId) {
            // Check if this is just the session we created internally (via sendMessage)
            const isOurNewSession = newSessionId === currentSessionIdRef.current;
            const isCreatingNewSession = previousSessionId === null && newSessionId !== null;
            const hasPendingMessages = pendingMessagesRef.current.length > 0;
            const isCurrentlyStreaming = isTypingRef.current || Boolean(streamingMessageIdRef.current);

            if ((isCreatingNewSession && hasPendingMessages) || (isOurNewSession && isCurrentlyStreaming)) {
                // Session was created by us during message send — don't clear anything
                activeSessionRef.current = newSessionId;
            } else {
                activeSessionRef.current = newSessionId;
                // Stop UI updates for the prior session's stream but keep the HTTP request
                // alive so the backend finishes creating the message even if the user
                // switches sessions mid-send. The event handler short-circuits on cancelledRef.
                // Stop UI updates for the prior session's stream but keep the HTTP request
                // alive so the backend finishes generating server-side. The event handler
                // short-circuits on cancelledRef, and the per-stream controllers stay in the
                // set until each stream naturally resolves and removes itself.
                streamCancelledRef.current = true;
                setMessages([]);
                pendingMessagesRef.current = [];
                setIsTyping(false);
                setStreamingMessageId(null);

                // Stash the outgoing session's unsent draft, restore the incoming one (if any).
                const previousDraftKey = previousSessionId ?? NEW_CHAT_DRAFT_KEY;
                sessionDraftsRef.current[previousDraftKey] = inputValueRef.current;
                const newDraftKey = newSessionId ?? NEW_CHAT_DRAFT_KEY;
                setInputValue(sessionDraftsRef.current[newDraftKey] ?? '');

                resetScroll();
            }

            // Refresh ONLY page 1 (not all pages of the infinite query) so the reconnect effect
            // can see a fresh `status: 'GENERATING'` without waiting on pages 2/3/4 to refetch.
            if (newSessionId && !isCreatingNewSession && !isOurNewSession && sessionAgentId) {
                const pageOneUrl = `/api/command-team/agents/${sessionAgentId}/sessions/${newSessionId}/messages?page=1&limit=30&sort_by=created_at&sort_order=desc`;
                fetchData(pageOneUrl)
                    .then((page) => {
                        queryClient.setQueryData(
                            ["command-messages", sessionAgentId, newSessionId],
                            (old: any) => {
                                if (!old?.pages?.length) {
                                    return { pages: [page], pageParams: [1] };
                                }
                                const pages = [...old.pages];
                                pages[0] = page;
                                return { ...old, pages };
                            },
                        );
                    })
                    .catch(() => {
                        // Cache stays as-is on failure — reconnect can still fire from stale data if present.
                    });
            }
        } else if (pendingMessagesRef.current.length === 0 && !newSessionId) {
            setMessages([]);
            resetScroll();
        }

        prevSessionIdRef.current = newSessionId;
    }, [initialSessionId, sessionAgentId, queryClient]);

    const {
        allMessages: fetchedMessages,
        isLoadingMessages,
        isFetchingMessages,
        isFetchingNextPage,
        hasNextPage,
        loadMoreRef,
        canListMessages,
        isMessagesError,
        messagesError,
    } = useCommandMessages(agentId, sessionIdForMessages);
    // Keep a ref so the reconnection effect can read latest messages without
    // depending on fetchedMessages (which changes on every pagination load).
    fetchedMessagesRef.current = fetchedMessages;

    const { allSessions: sessionsForStreamGate } = useCommandSessions(sessionAgentId || null);
    const sessionStreamActive = useMemo(() => {
        if (!sessionIdForMessages) return false;
        const row = sessionsForStreamGate.find((s) => s.id === sessionIdForMessages);
        return row?.status?.toUpperCase() === 'GENERATING';
    }, [sessionsForStreamGate, sessionIdForMessages]);

    /** Assistant message the server reports is still generating. Drives SSE reconnect and loader gating. */
    const generatingAssistantId = useMemo(() => {
        for (let i = fetchedMessages.length - 1; i >= 0; i--) {
            const m = fetchedMessages[i];
            if (m.role === 'assistant' && (m as { status?: string }).status === 'GENERATING') return m.id;
        }
        return null;
    }, [fetchedMessages]);

    useMergedMessages({
        fetchedMessages,
        isLoadingMessages,
        isFetchingMessages,
        sessionId: sessionIdForMessages,
        isTyping,
        streamingMessageId,
        pendingMessagesRef,
        setMessages,
    });

    const streamSupervisor = useStreamReconnect({
        agentId: sessionAgentId,
        sessionId: sessionIdForMessages,
        sessionStreamActive,
        generatingAssistantId,
        isLoadingMessages,
        fetchedMessagesRef,
        isTypingRef,
        streamingMessageIdRef,
        isTyping,
        streamingMessageId,
        streamAbortRef,
        setMessages,
        setIsTyping,
        setStreamingMessageId,
        setStreamingPhase,
        onSessionTitle: onSessionTitleUpdate,
        queryClient,
    });

    const { paginatedNewIds, reset: resetScroll } = useConversationScroll({
        messages,
        isFetchingNextPage,
        isLoadingMessages,
        conversationRef,
        stickyContextRef,
    });

    const createSessionMutation = useProtectedMutation({
        mutationFn: async () => {
            const response = await post(`/api/command-team/agents/${agentId}/sessions`, {});
            return response.data as CommandSession;
        },
        requiredPermissions: ['CREATE_COMMAND_SESSION'],
        onSuccess: (data: CommandSession) => {
            if (data?.id) {
                setCurrentSessionId(data.id);
                setIsFirstMessage(false);
                const now = new Date().toISOString();
                const newSession = {
                    ...data,
                    title: data.title ?? '',
                    status: data.status ?? 'active',
                    pinned: data.pinned ?? false,
                    message_count: data.message_count ?? 0,
                    total_tokens_used: data.total_tokens_used ?? 0,
                    created_at: data.created_at ?? now,
                    updated_at: data.updated_at ?? now,
                };
                queryClient.setQueryData(
                    ["command-sessions", agentId],
                    (old: any) => {
                        if (!old?.pages?.length) {
                            // No cache yet — create initial structure
                            return {
                                pages: [{ items: [newSession], item_count: 1, page_count: 1 }],
                                pageParams: [1],
                            };
                        }
                        const updatedPages = [...old.pages];
                        updatedPages[0] = {
                            ...updatedPages[0],
                            items: [newSession, ...(updatedPages[0].items ?? [])],
                            item_count: (updatedPages[0].item_count ?? 0) + 1,
                        };
                        return { ...old, pages: updatedPages };
                    }
                );
                if (onNewSessionCreated) {
                    onNewSessionCreated(data.id);
                }
            }
        },
        onError: (error: any) => {
            const isPermissionError =
                error?.message === "PERMISSION_DENIED" ||

                (typeof error?.response?.data?.message === "string" &&
                    error.response.data.message.toLowerCase().includes("permission"));
            if (isPermissionError) {
                setIsTyping(false);
                return;
            }
            const data = error?.response?.data;
            const description =
                (typeof data?.message === "string" && data.message) ||
                (typeof data?.detail === "string" && data.detail) ||
                (Array.isArray(data?.detail) && typeof data.detail[0]?.msg === "string" && data.detail[0].msg) ||
                (typeof data?.error === "string" && data.error) ||
                "We couldn't start a new chat. Please try again.";
            toast.error("Failed to create session", { description });
            setIsTyping(false);
        },
    });

    const { sendMessage, handleStopGeneration, isStopping } = useSendMessage({
        agentId: sessionAgentId,
        currentSessionId,
        isTyping,
        streamingMessageId,
        attachedFiles,
        createSessionMutation,
        clearAttachments,
        setInputValue,
        setCurrentSessionId,
        setMessages,
        setIsTyping,
        setStreamingMessageId,
        setStreamingPhase,
        streamCancelledRef,
        streamAbortRef,
        pendingMessagesRef,
        stickyContextRef,
        inputRef,
        onSessionTitle: onSessionTitleUpdate,
        queryClient,
        streamSupervisor,
    });
    const centerScheduleMutation = useCreateCommandScheduleJob();
    const user = useAppSelector((s) => s.user.user);

    const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback((e) => {
        e.preventDefault();

        if ((isTyping || streamingMessageId) && sessionAgentId && currentSessionId) {
            handleStopGeneration();
            return;
        }

        sendMessage(inputValue);
    }, [inputValue, isTyping, streamingMessageId, sessionAgentId, currentSessionId, handleStopGeneration, sendMessage]);
    const handleCenterScheduleSubmit = useCallback(
        ({ runAt, description, scheduleType, cronExpression, newSession }: CommandSchedulerSubmitInput) => {
            const trimmedMessage = inputValue.trim();
            if (!agentId) {
                toast.error('Agent is not available for scheduling');
                return;
            }
            if (!trimmedMessage) {
                toast.error('Add a message to schedule');
                return;
            }
            const userId = user?.id ?? '';
            const organizationId = user?.organization?.id ?? '';
            const commandTeamId = user?.organization?.command_team?.id ?? '';
            const analystTeamId = user?.organization?.analyst_team?.id?.trim() || null;
            const salesTeamId = user?.organization?.sales_team?.id?.trim() || null;
            const supportTeamId = user?.organization?.support_team?.id?.trim() || null;
            const tasksTeamId = user?.organization?.tasks_team?.id?.trim() || null;
            const computerUseTeamId = user?.organization?.computer_use_team?.id?.trim() || null;

            if (!userId || !organizationId || !commandTeamId) {
                toast.error('Account or organization is not loaded yet. Try again in a moment.');
                return;
            }

            const resolvedSessionId = sessionIdForMessages ?? createRandomSessionId();
            const fileIds = attachedFiles
                .filter((a) => a.status === 'done' && a.uploaded?.id)
                .map((a) => a.uploaded!.id);

            centerScheduleMutation.mutate(
                {
                    sessionId: resolvedSessionId,
                    newSession,
                    agentId,
                    prompt: trimmedMessage,
                    description,
                    runAt,
                    fileIds,
                    userId,
                    organizationId,
                    commandTeamId,
                    analystTeamId,
                    salesTeamId,
                    supportTeamId,
                    tasksTeamId,
                    computerUseTeamId,
                    scheduleType,
                    cronExpression,
                },
                {
                    onSuccess: () => {
                        setCenterScheduleOpen(false);
                        setInputValue('');
                        clearAttachments();
                    },
                }
            );
        },
        [inputValue, agentId, user, sessionIdForMessages, attachedFiles, centerScheduleMutation, clearAttachments]
    );

    const handlePromptClick = useCallback((promptText: string) => {
        setInputValue(promptText);
        if (isMobile) {
            inputRef.current?.blur();
        } else {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [isMobile]);

    const handlePickSelection = useCallback(
        (option: { label: string; value: string }, request: { toolCallId?: string }) => {
            sendMessage(option.label, {
                selection_value: option.value,
                selection_tool_call_id: request.toolCallId,
            });
        },
        [sendMessage],
    );
    const hasCenteredDraft =
        Boolean(inputValue.trim()) || attachedFiles.some((a) => a.status === 'done' && a.uploaded);
    const canSendCenteredPrompt =
        !createSessionMutation.isPending &&
        !attachedFiles.some((f) => f.status === 'uploading') &&
        hasCenteredDraft;
    const canStopGeneration = Boolean((isTyping || streamingMessageId) && sessionAgentId && currentSessionId);

    const handleCopy = useCallback((message: ExtendedChatMessage) => {
        let textToCopy = '';

        if (message.contentSegments && message.contentSegments.length > 0) {
            textToCopy = message.contentSegments
                .filter(seg => seg.type === 'text' && seg.content)
                .map(seg => seg.content)
                .join('\n\n');
        }

        if (!textToCopy && message.content) {
            textToCopy = message.content;
        }

        textToCopy = textToCopy
            .replace(/```+/g, '')
            .replace(/^Final Answer:\s*/i, '')
            .trim();

        if (textToCopy) {
            navigator.clipboard.writeText(textToCopy);
            toast.success('Copied to clipboard');
        } else {
            toast.error("No content to copy");
        }
    }, []);

    const regenerateMessageMutation = useProtectedMutation({
        mutationFn: async ({ messageId, sessionId, onEvent }: { messageId: string; sessionId: string; onEvent?: SSEEventHandler }) => {
            if (onEvent) {
                const abortController = new AbortController();
                streamAbortRef.current.add(abortController);

                try {
                    await postStream(
                        `/api/command-team/agents/${sessionAgentId}/sessions/${sessionId}/messages/${messageId}/regenerate`,
                        {},
                        (chunk: string) => processSSEChunk(chunk, onEvent),
                        abortController.signal
                    );
                } finally {
                    streamAbortRef.current.delete(abortController);
                }
                return { success: true };
            }
        },
        requiredPermissions: ['REGENERATE_COMMAND_MESSAGE'],
        onError: (error: any) => {
            const isAborted = error?.name === 'AbortError' || error?.message?.includes('aborted') || streamCancelledRef.current;
            if (!isAborted) {
                toast.error('Failed to regenerate message', {
                    description: error.response?.data?.message || error.message || 'An unexpected error occurred',
                });
                setIsTyping(false);
                setStreamingMessageId(null);
                setMessages(prev => prev.map(msg => {
                    if (msg.branches) {
                        return {
                            ...msg,
                            branches: msg.branches.map(branch =>
                                branch.isStreaming ? { ...branch, isStreaming: false } : branch
                            ),
                        };
                    }
                    return msg.isStreaming ? { ...msg, isStreaming: false } : msg;
                }));
            }
        },
    });

    const handleRegenerate = useCallback((messageId: string) => {
        if (!currentSessionId || isTyping) return;

        const messageIndex = messages.findIndex(m => m.id === messageId);
        if (messageIndex < 0) return;

        const originalMessage = messages[messageIndex];
        if (originalMessage.role !== 'assistant') return;

        const generationCount = originalMessage.branches?.length ?? 1;
        if (generationCount >= MAX_GENERATIONS_PER_MESSAGE) {
            toast.error("Maximum regenerations reached", {
                description: `You can generate up to ${MAX_GENERATIONS_PER_MESSAGE} responses per message`,
            });
            return;
        }

        const dbMessageId = originalMessage.dbId ||
            (messageId.startsWith('temp-') ? null : messageId);

        if (!dbMessageId) {
            toast.error("Cannot regenerate", {
                description: "Please wait for the message to be saved before regenerating",
            });
            return;
        }

        const tempBranchId = `temp-regen-${Date.now()}`;
        const newBranchMessage: ExtendedChatMessage = {
            id: tempBranchId,
            content: '',
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
            parentMessageId: messageId,
        };

        // A chat switch mutes old streams through this flag; a new regenerate must not inherit that.
        streamCancelledRef.current = false;
        setStreamingPhase('working');
        const eventHandler = createEventHandler({
            systemMessageId: tempBranchId,
            setMessages,
            onSessionTitle: onSessionTitleUpdate,
            cancelledRef: streamCancelledRef,
            setStreamingPhase,
        });

        setMessages(prev => prev.map(msg => {
            if (msg.id === messageId) {
                const existingBranches = msg.branches || [msg];
                return {
                    ...msg,
                    branches: [...existingBranches, newBranchMessage],
                    activeBranchIndex: existingBranches.length,
                };
            }
            return msg;
        }));

        setStreamingMessageId(tempBranchId);
        setIsTyping(true);

        regenerateMessageMutation.mutate(
            { messageId: dbMessageId, sessionId: currentSessionId, onEvent: eventHandler },
            {
                onSuccess: async () => {
                    // Mark the streaming branch as done but keep streamingMessageId
                    // alive so the temp-regen guard in the useEffect keeps blocking
                    // rebuilds while we wait for the refetch below.
                    setMessages(prev => prev.map(msg => {
                        if (msg.id === messageId && msg.branches) {
                            return {
                                ...msg,
                                branches: msg.branches.map(branch =>
                                    branch.isStreaming
                                        ? { ...branch, isStreaming: false }
                                        : branch
                                ),
                            };
                        }
                        return msg;
                    }));

                    // Refetch so the non-streaming merge sees the new ACTIVE message.
                    // We must NOT clear streamingMessageId before this resolves —
                    // if we do, the useEffect rebuild fires with stale data and wipes the branch.
                    await queryClient.refetchQueries({
                        queryKey: ['command-messages', sessionAgentId, currentSessionId],
                    });

                    // Release the streaming lock. Order matters:
                    // 1. setIsTyping(false) — non-streaming path won't run yet because
                    //    streamingMessageId (temp-regen-*) still blocks the guard.
                    // 2. setStreamingMessageId(null) — clears the guard; now the
                    //    non-streaming path runs with the freshly-fetched data.
                    setIsTyping(false);
                    setStreamingMessageId(null);
                },
            }
        );
    }, [messages, currentSessionId, isTyping, regenerateMessageMutation, onSessionTitleUpdate]);

    const handleBranchChange = useCallback((messageId: string, branchIndex: number) => {
        setMessages(prev => prev.map(msg => {
            if (msg.id === messageId) {
                return { ...msg, activeBranchIndex: branchIndex };
            }
            return msg;
        }));
    }, []);

    const hasMessages = messages.length > 0;
    /** Stream already running (local typing, reconnected SSE, or server-flagged GENERATING row). Hides the full-screen loader. */
    const streamActive = isTyping || Boolean(streamingMessageId) || Boolean(generatingAssistantId) || sessionStreamActive;
    /** Use only isLoadingMessages here, not isFetchingMessages — background refetch on tab focus must not replace the UI with the full-screen loader. */
    const isLoading =
        isLoadingAgent ||
        (Boolean(sessionIdForMessages) &&
            !hasMessages &&
            isLoadingMessages &&
            !streamActive);
    const showCenteredPrompt = !hasMessages && !isLoadingMessages && !isTyping && !isLoading && !streamActive;

    const isAccessDenied = Boolean(currentSessionId && !canListMessages);
    const isSessionNotFound =
        isMessagesError &&
        (messagesError as any)?.response?.data?.error_code === 'COMMAND_TEAM__SESSION_NOT_FOUND';
    const connectedIntegrationCount =
        (agent?.app_integrations ?? []).length +
        (agent?.custom_apps ?? []).length;

    const agentModelLabel = useMemo(() => {
        if (!agent?.llm_model) return undefined;
        const match = llmModelsData?.models?.find(
            (m) => m.model_key === agent.llm_model || m.model_id === agent.llm_model,
        );
        return match?.display_name ?? agent.llm_model;
    }, [agent?.llm_model, llmModelsData?.models]);

    return (
        <>
            {isSessionNotFound ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 py-12 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.88, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="flex flex-col items-center gap-5"
                    >
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-100 dark:bg-violet-900/30">
                            <MessageSquareX className="h-8 w-8 text-violet-500 dark:text-violet-400" />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                Session not found
                            </h2>
                            <p className="max-w-[320px] text-sm leading-relaxed text-muted-foreground">
                                This chat session no longer exists or may have been deleted. Start a new chat to continue.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => createSessionMutation.mutate()}
                            disabled={createSessionMutation.isPending}
                            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
                        >
                            {createSessionMutation.isPending
                                ? <Loader2Icon className="h-4 w-4 animate-spin" />
                                : <Plus className="h-4 w-4" />
                            }
                            Start a new chat
                        </button>
                    </motion.div>
                </div>
            ) : isAccessDenied ? (
                <div className="flex-1 flex items-center justify-center p-6">
                    <AccessDeniedCard message="You don't have permission to view messages in this session. Contact your administrator if you need access." />
                </div>
            ) : isLoading ? (
                <div className="flex-1 flex items-center justify-center">
                    <FluidOrb size="md" speed="fast" />
                </div>
            ) : hasMessages ? (
                <div
                    className="flex-1 flex flex-col min-h-0 w-full min-w-0 relative"
                    ref={conversationRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {isDraggingOver && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-background/90 backdrop-blur-sm pointer-events-none">
                            <Upload className="size-10 text-primary/70" aria-hidden />
                            <span className="text-sm font-medium text-foreground">Drop files here</span>
                            <span className="text-xs text-muted-foreground">PDF, images, docs, Excel, PowerPoint &middot; max 100 MB</span>
                        </div>
                    )}
                    <Conversation className="flex-1 w-full min-w-0" contextRef={stickyContextRef}>
                        <ConversationContent className="space-y-6 w-full max-w-full min-w-0">
                            <TrialPlanBanner />

                            {hasNextPage && (
                                <div ref={loadMoreRef} className="flex justify-center py-8">
                                    {isFetchingNextPage && <FluidOrb size="sm" speed="fast" />}
                                </div>
                            )}

                            {hasMessages ? (
                                <div className="max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-2 sm:px-4 min-w-0 space-y-6">
                                    {messages.map((message: ExtendedChatMessage, msgIndex: number) => {
                                        const hasBranches = message.branches && message.branches.length > 1;
                                        const activeBranchIndex = message.activeBranchIndex ?? 0;
                                        const activeMessage = hasBranches
                                            ? message.branches![activeBranchIndex]
                                            : message;
                                        const isPaginatedNew = paginatedNewIds.has(message.id);
                                        const userText =
                                            typeof activeMessage.content === 'string' ? activeMessage.content.trim() : '';
                                        const userHasAttachments =
                                            Boolean(activeMessage.attachedFiles && activeMessage.attachedFiles.length > 0);
                                        const showUserRow = message.role === 'user' && (userText.length > 0 || userHasAttachments);
                                        const showUserTextBubble = userText.length > 0;
                                        const hasAssistantError = Boolean(
                                            getAssistantErrorText(
                                                activeMessage.contentSegments,
                                                activeMessage.streamError
                                            )
                                        );
                                        const showAssistantRow =
                                            message.role === 'assistant' &&
                                            (hasAssistantError ||
                                                activeMessage.isStreaming ||
                                                (typeof activeMessage.content === 'string' &&
                                                    activeMessage.content.trim().length > 0) ||
                                                (activeMessage.contentSegments?.length ?? 0) > 0 ||
                                                (activeMessage.tools?.length ?? 0) > 0 ||
                                                (typeof activeMessage.reasoning === 'string' &&
                                                    activeMessage.reasoning.trim().length > 0) ||
                                                (activeMessage.sources?.length ?? 0) > 0 ||
                                                activeMessage.status === 'GENERATING');
                                        const selectionDisabled = msgIndex !== messages.length - 1;

                                        return (
                                            <motion.div
                                                key={`${message.id}-${activeBranchIndex}`}
                                                data-message-id={message.id}
                                                className="space-y-3 max-w-full min-w-0"
                                                initial={isPaginatedNew ? { opacity: 0, y: -8 } : false}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={isPaginatedNew ? { duration: 0.3, delay: msgIndex * 0.03, ease: 'easeOut' } : { duration: 0 }}
                                            >
                                                {/* User messages - no branching; text bubble only when there is visible text (attachment-only messages omit empty bubble) */}
                                                {showUserRow && (
                                                    <div className="group/user">
                                                        {userHasAttachments && (
                                                            <div className="mb-2 flex justify-end">
                                                                <MessageAttachments files={activeMessage.attachedFiles!} />
                                                            </div>
                                                        )}
                                                        {showUserTextBubble && (
                                                            <Message from="user" className="max-w-[min(100%,26rem)] sm:max-w-[min(100%,34rem)] md:max-w-[min(100%,40rem)]">
                                                                <MessageContent className="overflow-hidden break-words max-w-full text-sm leading-relaxed group-[.is-user]:rounded-2xl group-[.is-user]:rounded-br-none group-[.is-user]:bg-violet-50 group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground group-[.is-user]:shadow-none dark:group-[.is-user]:bg-violet-950/40 sm:group-[.is-user]:px-5 sm:group-[.is-user]:py-3.5">
                                                                    <CollapsibleUserMessage
                                                                        content={activeMessage.content}
                                                                        fadeFromClass="from-violet-50 dark:from-violet-950/40"
                                                                        toggleClassName="text-muted-foreground hover:text-foreground"
                                                                    />
                                                                </MessageContent>
                                                            </Message>
                                                        )}
                                                        {/* Timestamp + copy - visible on hover, right-aligned */}
                                                        <div className="flex items-center gap-1 justify-end opacity-0 group-hover/user:opacity-100 transition-opacity mt-1">
                                                            <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                                                {message.timestamp ? formatMessageTime(message.timestamp) : ''}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    if (userText) {
                                                                        navigator.clipboard.writeText(activeMessage.content);
                                                                        toast.success('Copied to clipboard');
                                                                    }
                                                                }}
                                                                className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                                                                aria-label="Copy message"
                                                            >
                                                                <CopyIcon className="size-3" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Assistant messages - with branching support; include GENERATING/reasoning/sources so first-load stubs still render */}
                                                {showAssistantRow && (
                                                    <div className="group/assistant">
                                                        {hasBranches ? (
                                                            <>
                                                            <Branch
                                                                defaultBranch={activeBranchIndex}
                                                                onBranchChange={(idx) => handleBranchChange(message.id, idx)}
                                                            >
                                                                <BranchMessages>
                                                                    {message.branches!.map((branch) => {
                                                                        const branchHasError = Boolean(
                                                                            getAssistantErrorText(
                                                                                branch.contentSegments,
                                                                                branch.streamError
                                                                            )
                                                                        );
                                                                        return (
                                                                        <Message
                                                                            from="assistant"
                                                                            key={branch.id}
                                                                            className={cn(
                                                                                branchHasError && 'max-w-full w-full'
                                                                            )}
                                                                        >
                                                                            <MessageContent
                                                                                className={cn(
                                                                                    'overflow-hidden break-words max-w-full',
                                                                                    branchHasError && 'w-full max-w-full'
                                                                                )}
                                                                            >
                                                                                <AssistantMessageContent
                                                                                    key={`content-${branch.id}`}
                                                                                    content={branch.content}
                                                                                    contentSegments={branch.contentSegments}
                                                                                    tools={branch.tools}
                                                                                    isStreaming={branch.isStreaming}
                                                                                    messageId={branch.id}
                                                                                    status={branch.status}
                                                                                    streamError={branch.streamError}
                                                                                    selectionDisabled={selectionDisabled}
                                                                                    onPickSelection={handlePickSelection}
                                                                                />
                                                                            </MessageContent>
                                                                        </Message>
                                                                        );
                                                                    })}
                                                                </BranchMessages>
                                                                <BranchSelector from="assistant">
                                                                    <BranchPrevious />
                                                                    <BranchPage />
                                                                    <BranchNext />
                                                                </BranchSelector>
                                                            </Branch>
                                                            {activeMessage.suggestions && activeMessage.suggestions.length > 0 ? (
                                                                <ContinueFlowSuggestions
                                                                    suggestions={activeMessage.suggestions}
                                                                    disabled={selectionDisabled}
                                                                    onPick={(suggestion) => sendMessage(suggestion)}
                                                                />
                                                            ) : null}
                                                            </>
                                                        ) : (
                                                            <Message
                                                                from="assistant"
                                                                className={cn(
                                                                    hasAssistantError && 'max-w-full w-full'
                                                                )}
                                                            >
                                                                <MessageContent
                                                                    className={cn(
                                                                        'overflow-hidden break-words max-w-full',
                                                                        hasAssistantError && 'w-full max-w-full'
                                                                    )}
                                                                >
                                                                    <AssistantMessageContent
                                                                        key={`content-${activeMessage.id}`}
                                                                        content={activeMessage.content}
                                                                        contentSegments={activeMessage.contentSegments}
                                                                        tools={activeMessage.tools}
                                                                        isStreaming={activeMessage.isStreaming}
                                                                        messageId={activeMessage.id}
                                                                        status={activeMessage.status}
                                                                        streamError={activeMessage.streamError}
                                                                        selectionDisabled={selectionDisabled}
                                                                        onPickSelection={handlePickSelection}
                                                                    />
                                                                    {activeMessage.suggestions && activeMessage.suggestions.length > 0 ? (
                                                                        <ContinueFlowSuggestions
                                                                            suggestions={activeMessage.suggestions}
                                                                            disabled={selectionDisabled}
                                                                            onPick={(suggestion) => sendMessage(suggestion)}
                                                                        />
                                                                    ) : null}
                                                                </MessageContent>
                                                            </Message>
                                                        )}

                                                        {/* Timestamp + actions for assistant messages - visible on hover */}
                                                        {!activeMessage.isStreaming && (
                                                            <div className="flex items-center gap-1 opacity-0 group-hover/assistant:opacity-100 transition-opacity mt-1">
                                                                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                                                    {activeMessage.timestamp ? formatMessageTime(activeMessage.timestamp) : ''}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleCopy(activeMessage)}
                                                                    className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                                                                    aria-label="Copy message"
                                                                >
                                                                    <CopyIcon className="size-3" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRegenerate(message.id)}
                                                                    disabled={
                                                                        regenerateMessageMutation.isPending ||
                                                                        !currentSessionId ||
                                                                        (message.branches?.length ?? 1) >= MAX_GENERATIONS_PER_MESSAGE
                                                                    }
                                                                    className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    aria-label={
                                                                        (message.branches?.length ?? 1) >= MAX_GENERATIONS_PER_MESSAGE
                                                                            ? `Maximum ${MAX_GENERATIONS_PER_MESSAGE} regenerations reached`
                                                                            : "Regenerate"
                                                                    }
                                                                    title={
                                                                        (message.branches?.length ?? 1) >= MAX_GENERATIONS_PER_MESSAGE
                                                                            ? `Maximum ${MAX_GENERATIONS_PER_MESSAGE} regenerations reached`
                                                                            : undefined
                                                                    }
                                                                >
                                                                    <RefreshCw className="size-3" />
                                                                </button>
                                                            </div>
                                                        )}

                                                        {/* Sources */}
                                                        {activeMessage.sources && activeMessage.sources.length > 0 && (
                                                            <Sources>
                                                                <SourcesTrigger count={activeMessage.sources.length} />
                                                                <SourcesContent>
                                                                    {activeMessage.sources.map((source, idx) => (
                                                                        <Source key={idx} href={source.url} title={source.title} />
                                                                    ))}
                                                                </SourcesContent>
                                                            </Sources>
                                                        )}
                                                    </div>
                                                )}
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </ConversationContent>
                        <ConversationScrollButton />
                    </Conversation>
                </div>
            ) : null}

            {showCenteredPrompt && !isAccessDenied && !isSessionNotFound && (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className="flex flex-1 flex-col min-h-0 w-full min-w-0 bg-background relative max-sm:overflow-y-auto"
                >
                    {isDraggingOver && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-background/90 backdrop-blur-sm pointer-events-none">
                            <Upload className="size-10 text-primary/70" aria-hidden />
                            <span className="text-sm font-medium text-foreground">Drop files here</span>
                            <span className="text-xs text-muted-foreground">PDF, images, docs, Excel, PowerPoint &middot; max 100 MB</span>
                        </div>
                    )}
                    <TrialPlanBanner />
                    <div className="mx-auto flex w-full max-w-[60rem] flex-1 flex-col min-h-0 min-w-0 px-4 max-sm:flex-none max-sm:pb-4 sm:px-6">
                        <motion.div
                            className="flex min-h-0 w-full flex-1 flex-col max-sm:flex-none max-sm:gap-5 sm:gap-8"
                            initial="hidden"
                            animate="visible"
                            variants={commandEmptyStagger}
                        >
                            {/* Hero — top on all breakpoints */}
                            <motion.div
                                className="order-1 shrink-0 space-y-3 pt-8 text-center max-sm:pt-6 sm:pt-[max(0.5rem,calc(14vh-6rem))] [@media(max-height:520px)]:pt-2 [@media(max-height:520px)]:space-y-1"
                                variants={commandEmptyItem}
                            >
                                <h1 className="font-bold text-foreground tracking-tight leading-snug text-[2.35rem] sm:text-[clamp(1.5rem,2.2vw+0.5rem,2.5rem)]">
                                    What do you want to know or do?
                                </h1>
                                <p className="inline-flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                                    <span className="size-1.5 rounded-full bg-[#36CFC9]" />
                                    {connectedIntegrationCount} Apps connected
                                </p>
                            </motion.div>

                            {/* Mobile: hero + flex space; categories + cards + input packed at bottom (Figma). Desktop: normal column flow. */}
                            <div className="order-2 flex min-h-0 w-full min-w-0 flex-1 flex-col max-sm:flex-none max-sm:gap-3 sm:justify-start sm:gap-8">
                            {/* Prompt categories */}
                            <motion.div
                                className="order-1 shrink-0 w-full min-w-0 max-sm:pt-0"
                                variants={commandEmptyItem}
                            >
                                <div className="-mt-0.5 w-full min-w-0 sm:mt-0">
                                    <HorizontalChevronChipRow
                                        itemCount={
                                            1 +
                                            categoriesScroll.length +
                                            (isLoadingPresetParents &&
                                            parentCategoryItems.length === 0
                                                ? 1
                                                : 0)
                                        }
                                    >
                                        <div className="flex w-max min-w-0 flex-nowrap items-center gap-3 sm:gap-4">
                                            <motion.button
                                                type="button"
                                                whileHover={{ scale: 1 }}
                                                whileTap={{ scale: 0.98 }}
                                                transition={{ type: 'spring', stiffness: 450, damping: 28 }}
                                                onClick={() => {
                                                    if (isMobile) inputRef.current?.blur();
                                                    setActiveCategory((prev) =>
                                                        prev === PROMPT_CATEGORY_FAVOURITES ? '' : PROMPT_CATEGORY_FAVOURITES
                                                    );
                                                }}
                                                className={cn(
                                                    'group shrink-0 inline-flex h-7 items-center gap-1.5 rounded-full border border-[#D7D7D7] bg-background px-2.5 text-xs transition-colors sm:h-6',
                                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                                                    'dark:border-zinc-700 dark:bg-zinc-800/70 dark:text-zinc-300',
                                                    activeCategory === PROMPT_CATEGORY_FAVOURITES
                                                        ? 'font-medium text-[#1E1E1E] dark:border-zinc-500 dark:bg-zinc-800 dark:text-zinc-100'
                                                        : 'text-[#717171] hover:border-[#C7C7C7] hover:text-[#1E1E1E] dark:hover:border-zinc-600 dark:hover:text-zinc-100'
                                                )}
                                            >
                                                <Star
                                                    className={cn(
                                                        'size-3.5 shrink-0 stroke-[1.5] transition-colors',
                                                        activeCategory === PROMPT_CATEGORY_FAVOURITES
                                                            ? 'fill-[#F5B301] text-[#F5B301]'
                                                            : 'fill-transparent text-[#8A8A8A] group-hover:text-[#1E1E1E] dark:text-zinc-500 dark:group-hover:text-zinc-200'
                                                    )}
                                                />
                                                Favourites
                                            </motion.button>
                                            {isLoadingPresetParents && parentCategoryItems.length === 0 ? (
                                                <Loader2Icon
                                                    className="size-4 shrink-0 animate-spin text-muted-foreground"
                                                    aria-label="Loading categories"
                                                />
                                            ) : null}
                                            {categoriesScroll.map((cat) => {
                                                const parentItem =
                                                    cat === PROMPT_CATEGORY_CUSTOM
                                                        ? null
                                                        : parentCategoryItems.find((i) => i.id === cat);
                                                const label =
                                                    cat === PROMPT_CATEGORY_CUSTOM
                                                        ? PROMPT_CATEGORY_CUSTOM
                                                        : presetParentCategoryPillLabel(parentItem?.name ?? '');
                                                return (
                                                <motion.button
                                                    key={cat}
                                                    type="button"
                                                    whileHover={{ scale: 1 }}
                                                    whileTap={{ scale: 0.97 }}
                                                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                                    onClick={() => {
                                                        if (isMobile) inputRef.current?.blur();
                                                        setActiveCategory((prev) => (prev === cat ? '' : cat));
                                                    }}
                                                    className={cn(
                                                        promptCategoryChipBase,
                                                        'whitespace-nowrap',
                                                        activeCategory === cat
                                                            ? promptCategoryChipActive
                                                            : promptCategoryChipInactive
                                                    )}
                                                >
                                                    {label}
                                                </motion.button>
                                            );
                                            })}
                                        </div>
                                    </HorizontalChevronChipRow>
                                </div>
                            </motion.div>

                            {/* Preset cards — directly above composer on mobile; desktop: full grid below composer */}
                            <div className="order-2 flex shrink-0 flex-col max-sm:pb-2 pb-3 sm:order-3 sm:shrink-0 sm:pb-12 [@media(max-height:520px)]:hidden">
                                <motion.div
                                    className="w-full min-w-0 overflow-hidden"
                                    animate={{
                                        height: activeCategory !== '' ? 'auto' : 0,
                                        opacity: activeCategory !== '' ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <AnimatePresence mode="wait">
                                        {activeCategory !== '' && isLoadingActiveCategoryPresets && (
                                            <motion.div
                                                key="presets-skeleton"
                                                className="flex flex-nowrap gap-3 overflow-x-auto overflow-y-hidden sm:gap-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                {Array.from({ length: 3 }).map((_, i) => (
                                                    <div key={i} className="max-sm:w-[75vw] max-sm:min-w-[75vw] sm:w-[290px] sm:min-w-[290px] rounded-xl border border-border bg-card p-3 pt-2.5 sm:p-4 sm:pt-3 space-y-2 sm:space-y-3 border-t-4 border-t-muted shrink-0 [&_*]:![animation-duration:0.8s]">
                                                        <div className="flex items-center gap-2">
                                                            <Skeleton className="h-4 w-12 rounded-full" />
                                                            <Skeleton className="h-3 w-20" />
                                                        </div>
                                                        <Skeleton className="h-5 w-full" />
                                                        <Skeleton className="h-5 w-3/4" />
                                                        <div className="rounded-lg bg-muted/40 p-3 space-y-2">
                                                            <Skeleton className="h-3 w-24" />
                                                            <Skeleton className="h-3 w-full" />
                                                        </div>
                                                        <Skeleton className="h-3 w-32" />
                                                    </div>
                                                ))}
                                            </motion.div>
                                        )}
                                        {activeCategory !== '' && !isLoadingActiveCategoryPresets && promptLibraryPrompts.length === 0 && activeCategory === PROMPT_CATEGORY_FAVOURITES && (
                                            <motion.div
                                                key="favourites-empty"
                                                className="flex flex-col items-center justify-center gap-2 px-3 py-10 text-center"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.25, ease: commandEmptyEase }}
                                            >
                                                <Star
                                                    className="size-7 text-muted-foreground/40"
                                                    aria-hidden
                                                />
                                                <p className="text-sm font-medium leading-tight text-foreground">
                                                    No Preset here
                                                </p>
                                                <p className="max-w-md text-xs text-muted-foreground">
                                                    Explore quick commands by selecting{' '}
                                                    <span className="font-medium text-foreground">Executive</span> or{' '}
                                                    <span className="font-medium text-foreground">Manager</span> above
                                                </p>
                                            </motion.div>
                                        )}
                                        {activeCategory !== '' && !isLoadingActiveCategoryPresets && (promptLibraryPrompts.length > 0 || activeCategory !== PROMPT_CATEGORY_FAVOURITES) && (
                                            <motion.div
                                                key={`${activeCategory}-${promptParentTabMode}`}
                                                initial={{ opacity: 0, y: 5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 3 }}
                                                transition={{ duration: 0.28, ease: commandEmptyEase }}
                                            >
                                                <PresetPromptCards
                                                    prompts={promptLibraryPrompts}
                                                    mode={promptParentTabMode}
                                                    favouriteIds={mergedFavouriteIds}
                                                    onToggleFavourite={togglePromptFavourite}
                                                    onSelectPrompt={handlePromptClick}
                                                    className=""
                                                    onScrollContainerReady={handlePresetCardsScrollRoot}
                                                    loadMoreRef={
                                                        hasNextActiveCategoryPresets ? activePresetsLoadMoreRef : undefined
                                                    }
                                                    isFetchingMore={isFetchingNextActiveCategoryPresets}
                                                />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            </div>

                            {/* Composer — bottom of cluster on mobile; desktop: between chips and card grid */}
                            <motion.div
                                className="order-3 flex w-full min-w-0 flex-col sm:order-2"
                                variants={commandEmptyItem}
                            >
                            <PromptInput
                                onSubmit={handleSubmit}
                                className={cn(
                                    "command-spin-outline relative w-full min-w-0 overflow-visible rounded-[1.1rem] border border-border bg-background shadow-sm divide-y-0 p-0 dark:border-border flex flex-col justify-center min-h-[144px]",
                                )}
                            >
                                <input ref={fileInputRef} type="file" accept={ACCEPT_FILES} multiple className="hidden" aria-label="Upload files" onChange={handleFileSelect} />
                                <div className="min-w-0 space-y-0">
                                    <div
                                        className="relative flex min-h-0 min-w-0 flex-col overflow-hidden rounded-[1.75rem] transition-colors"
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        aria-label="Drop files here to attach"
                                    >
                                        {isDraggingOver && (
                                            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-[1.75rem] border-2 border-dashed border-border/50 bg-background/95 pointer-events-none dark:border-border">
                                                <Upload className="size-6 text-muted-foreground" aria-hidden />
                                                <span className="text-xs text-muted-foreground">Drop files here</span>
                                                <span className="text-[11px] text-muted-foreground/80">PDF, images, docs, Excel · max 100MB</span>
                                            </div>
                                        )}
                                        {attachedFiles.length > 0 && (
                                            <div className="relative min-w-0 px-3 pt-2 pb-1">
                                                {canScrollAttachedLeft && (
                                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); scroll(attachedScrollRef.current, 'left', setCanScrollAttachedLeft, setCanScrollAttachedRight); }} className="absolute left-1 top-1/2 z-10 -translate-y-1/2 size-8 rounded-full border border-border bg-background/90 backdrop-blur-sm shadow-md hover:bg-muted transition-colors flex items-center justify-center" aria-label="Scroll attachments left">
                                                        <ChevronLeft className="size-4 text-foreground" />
                                                    </button>
                                                )}
                                                {canScrollAttachedRight && (
                                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); scroll(attachedScrollRef.current, 'right', setCanScrollAttachedLeft, setCanScrollAttachedRight); }} className="absolute right-1 top-1/2 z-10 -translate-y-1/2 size-8 rounded-full border border-border bg-background/90 backdrop-blur-sm shadow-md hover:bg-muted transition-colors flex items-center justify-center" aria-label="Scroll attachments right">
                                                        <ChevronRight className="size-4 text-foreground" />
                                                    </button>
                                                )}
                                                <div ref={setAttachedRef} onScroll={() => requestAnimationFrame(() => check(attachedScrollRef.current, setCanScrollAttachedLeft, setCanScrollAttachedRight))} className={cn('overflow-x-auto min-w-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]', canScrollAttachedLeft && 'pl-1', canScrollAttachedRight && 'pr-1')}>
                                                    <div className="flex w-max flex-nowrap items-center gap-2">
                                                        {attachedFiles.map((item, i) => (
                                                            <span key={item.localId} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border/50 bg-muted/70 px-2.5 py-1.5 text-xs">
                                                                {item.status === 'uploading' && <Loader2Icon className="size-3 shrink-0 animate-spin text-primary" />}
                                                                <span className="max-w-[150px] truncate">{item.file.name}</span>
                                                                <button type="button" onClick={() => removeAttachedFile(i)} className="shrink-0 rounded p-0.5 hover:bg-muted" aria-label="Remove file">
                                                                    <XIcon className="size-3" />
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <PromptInputTextarea
                                            ref={inputRef}
                                            value={inputValue}
                                            onChange={(e) => setInputValue(e.target.value)}
                                            onPaste={handlePaste}
                                            onFocus={() => {
                                                if (isMobile) {
                                                    if (activeCategory !== '') lastActiveCategoryRef.current = activeCategory;
                                                    setActiveCategory('');
                                                }
                                            }}
                                            placeholder="Talk to your business..."
                                            disabled={createSessionMutation.isPending}
                                            className="min-w-0 w-full !border-0 !shadow-none bg-transparent placeholder:text-muted-foreground focus-visible:!ring-0 !resize-none text-[15px] leading-snug !rounded-none !px-5 !pt-4 !pb-2 dark:placeholder:text-muted-foreground"
                                            minHeight={84}
                                            maxHeight={isMobile ? 112 : 220}
                                        />
                                        <div className="flex min-w-0 items-center gap-3 border-t border-border px-3 py-1.5 dark:border-border">
                                            <button
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => fileInputRef.current?.click()}
                                                className="flex size-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground dark:text-muted-foreground"
                                                aria-label="Attach files"
                                            >
                                                <Plus className="size-[1.125rem] stroke-[1.75]" />
                                            </button>
                                            <div className="h-4 w-px bg-border dark:bg-border shrink-0 mx-1" />
                                            <button
                                                type="button"
                                                className={COMPOSER_TOOLBAR_PILL_CLASS}
                                                aria-label="Schedule message"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => setCenterScheduleOpen(true)}
                                                disabled={
                                                    centerScheduleMutation.isPending ||
                                                    !inputValue.trim() ||
                                                    !agentId
                                                }
                                            >
                                                <CalendarClock className="size-3.5 shrink-0" strokeWidth={2} />
                                                <span>Schedule</span>
                                            </button>
                                            <div className="flex-1" />
                                            <button
                                                type="submit"
                                                className={cn(
                                                    "inline-flex size-10 shrink-0 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed",
                                                    canStopGeneration || canSendCenteredPrompt
                                                        ? "bg-[#6B46FF] text-white hover:bg-[#5B36E6]"
                                                        : "bg-[#ECE7FF] text-white/80 dark:bg-zinc-800 dark:text-zinc-500 dark:ring-1 dark:ring-inset dark:ring-zinc-700"
                                                )}
                                                disabled={canStopGeneration ? isStopping : !canSendCenteredPrompt}
                                                aria-label={canStopGeneration ? 'Stop generation' : 'Send message'}
                                            >
                                                {canStopGeneration ? (
                                                    isStopping ? (
                                                        <Loader2Icon className="size-5 animate-spin" aria-hidden />
                                                    ) : (
                                                        <SquareIcon className="size-5" aria-hidden />
                                                    )
                                                ) : createSessionMutation.isPending ? (
                                                    <Loader2Icon className="size-5 animate-spin" aria-hidden />
                                                ) : (
                                                    <Send className="size-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </PromptInput>
                            </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}

            {!showCenteredPrompt && !isLoading && !isAccessDenied && !isSessionNotFound && (
                <div className="w-full px-2 sm:px-4 md:px-6 lg:px-4 min-w-0 space-y-3 pb-safe pb-3 lg:pb-4">
                    <PromptInput
                        onSubmit={handleSubmit}
                        className={cn(
                            "command-spin-outline relative w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto min-w-0 overflow-visible rounded-[1.75rem] border border-border bg-background shadow-sm divide-y-0 p-0 dark:border-border",
                        )}
                    >
                        <input ref={fileInputRef} type="file" accept={ACCEPT_FILES} multiple className="hidden" aria-label="Upload files" onChange={handleFileSelect} />
                        <div className="min-w-0 space-y-0">
                            <div
                                className="relative flex min-h-0 min-w-0 flex-col overflow-hidden rounded-[1.75rem] transition-colors"
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                aria-label="Drop files here to attach"
                            >
                                {isDraggingOver && (
                                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-[1.75rem] border-2 border-dashed border-border/50 bg-background/95 pointer-events-none dark:border-border">
                                        <Upload className="size-6 text-muted-foreground" aria-hidden />
                                        <span className="text-xs text-muted-foreground">Drop files here</span>
                                        <span className="text-[11px] text-muted-foreground/80">PDF, images, docs, Excel · max 100MB</span>
                                    </div>
                                )}
                                {/* Attached files row */}
                                {attachedFiles.length > 0 && (
                                    <div className="relative min-w-0 px-5 pt-3 pb-0">
                                        {canScrollAttachedLeft && (
                                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); scroll(attachedScrollRef.current, 'left', setCanScrollAttachedLeft, setCanScrollAttachedRight); }} className="absolute left-1 top-1/2 z-10 -translate-y-1/2 size-8 rounded-full border border-border bg-background/90 backdrop-blur-sm shadow-md hover:bg-muted transition-colors flex items-center justify-center" aria-label="Scroll left">
                                                <ChevronLeft className="size-4 text-foreground" />
                                            </button>
                                        )}
                                        {canScrollAttachedRight && (
                                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); scroll(attachedScrollRef.current, 'right', setCanScrollAttachedLeft, setCanScrollAttachedRight); }} className="absolute right-1 top-1/2 z-10 -translate-y-1/2 size-8 rounded-full border border-border bg-background/90 backdrop-blur-sm shadow-md hover:bg-muted transition-colors flex items-center justify-center" aria-label="Scroll right">
                                                <ChevronRight className="size-4 text-foreground" />
                                            </button>
                                        )}
                                        <div ref={setAttachedRef} onScroll={() => requestAnimationFrame(() => check(attachedScrollRef.current, setCanScrollAttachedLeft, setCanScrollAttachedRight))} className={cn('overflow-x-auto min-w-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]', canScrollAttachedLeft && 'pl-1', canScrollAttachedRight && 'pr-1')}>
                                            <div className="flex w-max flex-nowrap items-center gap-2">
                                                {attachedFiles.map((item, i) => (
                                                    <span key={item.localId} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border/50 bg-muted/70 px-2.5 py-1.5 text-xs">
                                                        {item.status === 'uploading' && <Loader2Icon className="size-3 shrink-0 animate-spin text-primary" />}
                                                        <span className="max-w-[150px] truncate">{item.file.name}</span>
                                                        <button type="button" onClick={() => removeAttachedFile(i)} className="shrink-0 rounded p-0.5 hover:bg-muted" aria-label="Remove file">
                                                            <XIcon className="size-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* Textarea */}
                                <PromptInputTextarea
                                    ref={inputRef}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onPaste={handlePaste}
                                    placeholder="Talk to your business..."
                                    disabled={createSessionMutation.isPending}
                                    className="min-w-0 w-full !border-0 !shadow-none bg-transparent placeholder:text-muted-foreground focus-visible:!ring-0 !resize-none text-[15px] leading-snug !rounded-none !px-5 !pt-4 !pb-2 dark:placeholder:text-muted-foreground"
                                    minHeight={52}
                                    maxHeight={168}
                                />
                                {/* Bottom toolbar: + | schedule | spacer | send */}
                                <div className="flex min-w-0 items-center gap-3 border-t border-border px-3 py-1.5 dark:border-border">
                                    <button
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => fileInputRef.current?.click()}
                                        className="flex size-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground dark:text-muted-foreground"
                                        aria-label="Attach files"
                                    >
                                        <Plus className="size-[1.125rem] stroke-[1.75]" />
                                    </button>
                                    <div className="h-4 w-px bg-border dark:bg-border shrink-0 mx-1" />
                                    <button
                                        type="button"
                                        className={COMPOSER_TOOLBAR_PILL_CLASS}
                                        aria-label="Schedule message"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => setCenterScheduleOpen(true)}
                                        disabled={
                                            centerScheduleMutation.isPending ||
                                            !inputValue.trim() ||
                                            !agentId
                                        }
                                    >
                                        <CalendarClock className="size-3.5 shrink-0" strokeWidth={2} />
                                        <span>Schedule</span>
                                    </button>
                                    <ComposerPresetsButton
                                        onSelectPrompt={handlePromptClick}
                                        disabled={createSessionMutation.isPending}
                                    />
                                    <div className="flex-1" />
                                    <button
                                        type="submit"
                                        className={cn(
                                            "inline-flex size-7 shrink-0 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed",
                                            canStopGeneration || canSendCenteredPrompt
                                                ? "bg-[#6B46FF] text-white hover:bg-[#5B36E6]"
                                                : "bg-[#ECE7FF] text-white/80 dark:bg-zinc-800 dark:text-zinc-500 dark:ring-1 dark:ring-inset dark:ring-zinc-700"
                                        )}
                                        disabled={canStopGeneration ? isStopping : !canSendCenteredPrompt}
                                        aria-label={canStopGeneration ? 'Stop generation' : 'Send message'}
                                    >
                                        {canStopGeneration ? (
                                            isStopping ? (
                                                <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
                                            ) : (
                                                <SquareIcon className="size-3.5" aria-hidden />
                                            )
                                        ) : createSessionMutation.isPending ? (
                                            <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
                                        ) : (
                                            <Send className="size-3.5" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </PromptInput>
                </div>
            )}
            <AgentSettingsSheet
                agent={agent}
                isLoadingAgent={isLoadingAgent}
                disabled={isTyping || createSessionMutation.isPending}
                agentId={agentId}
                open={settingsSheetOpen}
                onOpenChange={setSettingsSheetOpen}
            />
            <CommandSchedulerDialog
                open={centerScheduleOpen}
                onOpenChange={setCenterScheduleOpen}
                isSubmitting={centerScheduleMutation.isPending}
                onSubmit={handleCenterScheduleSubmit}
                agentName={agent?.name}
                agentModelLabel={agentModelLabel}
                hasExistingSession={!!sessionIdForMessages}
            />
        </>
    );
}
